import React, { useState, useEffect } from 'react'
import { Trash2, Edit2 } from 'lucide-react'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import SidePanel from '@/components/ui/SidePanel'
import DeleteConfirmModal from '@/components/ui/DeleteConfirmModal'
import { designTokens } from '@/utils/design-tokens'
import { PerformanceTreeNode } from '../types'
import { getPerformanceTypeLabel } from '../utils/performanceUtils'
import { BusinessService } from '@/api/generated/services/BusinessService'
import { PerformanceGroupService } from '@/api/generated/services/PerformanceGroupService'
import { PerformanceCategoryService } from '@/api/generated/services/PerformanceCategoryService'
import { PerformanceClassService } from '@/api/generated/services/PerformanceClassService'
import { PerformanceService } from '@/api/generated/services/PerformanceService'
import type { BusinessResponse } from '@/api/generated/models/BusinessResponse'
import type { PerformanceGroupResponse } from '@/api/generated/models/PerformanceGroupResponse'
import type { PerformanceCategoryResponse } from '@/api/generated/models/PerformanceCategoryResponse'
import type { PerformanceClassResponse } from '@/api/generated/models/PerformanceClassResponse'
import type { PerformanceResponse } from '@/api/generated/models/PerformanceResponse'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'

/** 패널이 처리하는 5가지 유형 */
export type PerformanceNodeType = PerformanceTreeNode['type']

// ===== Type adapters: generated GET responses → PerformanceTreeNode =====
// 각 단건 GET 응답이 panel 표시에 충분한 필드를 모두 가지므로 단순 매핑.

function adaptBusiness(r: BusinessResponse): PerformanceTreeNode {
  return {
    id: r.id,
    name: r.name,
    type: 'business',
    businessId: r.id,
    targetValue: null,
    actualValue: null,
    achievementRate: null,
    assignee: null,
    department: null,
    children: [],
    orgHierarchy: [r.name],
    workspace_id: r.workspace_id,
    workspace_name: r.workspace_name,
    business_id: r.id,
    business_name: r.name,
    sort_key: r.sort_key,
  }
}

function adaptGroup(r: PerformanceGroupResponse): PerformanceTreeNode {
  return {
    id: r.id,
    name: r.name,
    type: 'performance_group',
    businessId: r.business_id,
    targetValue: null,
    actualValue: null,
    achievementRate: r.figure_percentage,
    assignee: null,
    department: null,
    children: [],
    // [사업명, 자기이름] — panel은 orgHierarchy[0]만 사용해 사업명 표시
    orgHierarchy: [r.business_name, r.name],
    workspace_id: r.workspace_id,
    workspace_name: r.workspace_name,
    business_id: r.business_id,
    business_name: r.business_name,
    sort_key: r.sort_key,
  }
}

function adaptCategory(r: PerformanceCategoryResponse): PerformanceTreeNode {
  // [사업명, 지표구분명, ...root→직속부모 카테고리 체인, 자기이름]
  const ancestors = r.ancestor_category_names ?? []
  return {
    id: r.id,
    name: r.name,
    type: 'performance_category',
    businessId: r.business_id,
    targetValue: null,
    actualValue: null,
    achievementRate: r.figure_percentage,
    assignee: null,
    department: null,
    children: [],
    orgHierarchy: [r.business_name, r.group_name, ...ancestors, r.name],
    groupId: r.group_id,
    parentId: r.parent_id,
    weight: r.weight,
    manual_weight: r.manual_weight,
    sort_key: r.sort_key,
  }
}

function adaptClass(r: PerformanceClassResponse): PerformanceTreeNode {
  // [사업명, 지표구분명, ...ancestor 카테고리들, 직속 카테고리명, 자기이름]
  const ancestors = r.ancestor_category_names ?? []
  return {
    id: r.id,
    name: r.name,
    type: 'performance_class',
    businessId: r.business_id,
    targetValue: r.target_figure,
    actualValue: r.done_figure,
    achievementRate: r.achievement_rate,
    assignee: null,
    department: null,
    children: [],
    orgHierarchy: [
      r.business_name,
      r.group_name,
      ...ancestors,
      r.performance_category_name,
      r.name,
    ],
    performance_category_id: r.performance_category_id,
    weight: r.weight,
    manual_weight: r.manual_weight,
    target_figure_formula: r.target_figure_formula,
    done_figure_formula: r.done_figure_formula,
    sort_key: r.sort_key,
  }
}

function adaptPerformance(r: PerformanceResponse): PerformanceTreeNode {
  // PerformanceResponse는 ancestor_category_names를 제공하지 않으므로
  // 직속 체인(사업 > 지표구분 > 카테고리 > 성과지표 > 세부지표)까지만 표시
  const hierarchy: string[] = []
  if (r.business_name) hierarchy.push(r.business_name)
  if (r.group_name) hierarchy.push(r.group_name)
  if (r.category_name) hierarchy.push(r.category_name)
  if (r.class_name) hierarchy.push(r.class_name)
  hierarchy.push(r.name)

  return {
    id: r.id,
    name: r.name,
    type: 'performance',
    businessId: r.business_id ?? undefined,
    targetValue: r.target_figure,
    actualValue: r.done_figure,
    achievementRate: r.figure_percentage,
    assignee: r.assignee_id
      ? {
          id: r.assignee_id,
          name: r.assignee_name,
          email: '',
          phone: '',
          account_id: '',
        }
      : null,
    department: r.department_id
      ? { id: r.department_id, name: r.department_name }
      : null,
    children: [],
    orgHierarchy: hierarchy,
    business_id: r.business_id ?? undefined,
    business_name: r.business_name ?? undefined,
    linkType: r.link_type === 'task' ? 'task' : 'direct',
    unit: r.unit,
    status: r.status,
    task_id: r.task_id ?? null,
    map_id: r.map_id != null ? String(r.map_id) : undefined,
    formular_key: r.formular_key ?? undefined,
    sort_key: r.sort_key ?? null,
  }
}

async function fetchPerformanceByType(
  id: string,
  type: PerformanceNodeType,
): Promise<PerformanceTreeNode> {
  switch (type) {
    case 'business':
      return adaptBusiness(await BusinessService.appsBusinessViewsGetBusiness({ businessId: id }))
    case 'performance_group':
      return adaptGroup(await PerformanceGroupService.appsPerformanceViewsGetPerformanceGroup({ groupId: id }))
    case 'performance_category':
      return adaptCategory(await PerformanceCategoryService.appsPerformanceViewsGetPerformanceCategory({ categoryId: id }))
    case 'performance_class':
      return adaptClass(await PerformanceClassService.appsPerformanceViewsGetPerformanceClass({ classId: id }))
    case 'performance':
      return adaptPerformance(await PerformanceService.appsPerformanceViewsGetPerformance({ performanceId: id }))
  }
}

interface PerformanceDetailPanelProps {
  isOpen: boolean
  onClose: () => void
  /** 표시할 항목의 ID. null이면 패널 내용 비움 */
  performanceId: string | null
  /** 표시할 항목의 유형 (5종) */
  performanceType: PerformanceNodeType | null
  /** 부모가 수정/삭제 성공 후 이 값을 증가시키면 패널이 자체적으로 재조회 */
  refreshKey?: number
  /** 수정 버튼 클릭 시: 패널이 내부 로드한 PerformanceTreeNode를 부모에 전달 */
  onEdit?: (performance: PerformanceTreeNode) => void
  /** 삭제 확정 시 부모에 위임. 부모가 실제 DELETE API 호출 + 후처리 */
  onDelete?: (performance: PerformanceTreeNode) => void
  /** 삭제 성공 후 부모 리스트 새로고침용 */
  onSuccess?: () => void
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    className="pb-2 border-b"
    style={{
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)


const PerformanceDetailPanel: React.FC<PerformanceDetailPanelProps> = ({
  isOpen,
  onClose,
  performanceId,
  performanceType,
  refreshKey,
  onEdit,
  onDelete,
  onSuccess,
}) => {
  const { showError } = useAlert()

  // ===== 자체 데이터 로드 =====
  // 5가지 유형별로 단건 GET API를 호출하고 adapter로 PerformanceTreeNode 변환.
  const [loadedPerformance, setLoadedPerformance] = useState<PerformanceTreeNode | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // 패널이 닫힐 때 모달 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setIsDeleteModalOpen(false)
      setIsDeleting(false)
      setDeleteError(null)
      setLoadError(null)
    }
  }, [isOpen])

  // 노드 로드 — type별 단건 GET 호출
  useEffect(() => {
    if (!isOpen || !performanceId || !performanceType) {
      setLoadedPerformance(null)
      return
    }
    let cancelled = false
    setIsLoading(true)
    setLoadError(null)
    fetchPerformanceByType(performanceId, performanceType)
      .then(node => {
        if (!cancelled) setLoadedPerformance(node)
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load performance detail:', err)
        handleApiError(err, showError, ErrorMessages.FETCH_FAILED)
        setLoadError('성과 데이터를 불러오지 못했습니다.')
        setLoadedPerformance(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, performanceId, performanceType, refreshKey, showError])

  // ===== 패널이 닫혀있으면 아무것도 렌더하지 않음 =====
  if (!isOpen) return null

  // ===== 로딩/에러/빈 상태 → 패널은 열린 채로 placeholder =====
  if (!loadedPerformance) {
    return (
      <SidePanel
        isOpen={isOpen}
        onClose={onClose}
        title="상세"
        width={800}
      >
        <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
          {loadError ? (
            <span style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.error.DEFAULT,
            }}>
              {loadError}
            </span>
          ) : isLoading ? (
            <div className="flex flex-col items-center gap-3">
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2"
                style={{ borderColor: designTokens.colors.primary.DEFAULT }}
              />
              <span style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '14px',
                color: designTokens.colors.gray[5],
              }}>
                데이터를 불러오는 중...
              </span>
            </div>
          ) : (
            <span style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[4],
            }}>
              표시할 항목이 없습니다.
            </span>
          )}
        </div>
      </SidePanel>
    )
  }

  const getTypeColor = (type: string, linkType?: string) => {
    const label = getPerformanceTypeLabel(
      type as PerformanceNodeType,
      linkType,
    )

    const colorConfig = {
      '사업': 'purple' as const,
      '지표구분': 'info' as const,
      '하위분류': 'info' as const,
      '성과지표': 'info' as const,
      '세부지표': 'info' as const,
      '연결세부지표': 'warning' as const,
    }

    return colorConfig[label as keyof typeof colorConfig] || 'gray' as const
  }

  const getHierarchyDisplay = (performance: PerformanceTreeNode) => {
    const { type, orgHierarchy } = performance

    switch (type) {
      case 'business':
        return '-'
      case 'performance_group':
        return orgHierarchy[0] || '-'
      case 'performance_category':
        return orgHierarchy.slice(0, -1).join(' > ') || '-'
      case 'performance_class':
        return orgHierarchy.slice(0, -1).join(' > ') || '-'
      case 'performance':
        return orgHierarchy.slice(0, -1).join(' > ') || '-'
      default:
        return '-'
    }
  }

  const formatValue = (value: number | null) => {
    if (value === null) return '-'
    return new Intl.NumberFormat('ko-KR').format(value)
  }

  const formatRate = (rate: number | null) => {
    if (rate === null) return '-'
    return `${rate.toFixed(1)}%`
  }

  const headerActions = loadedPerformance.type === 'business' ? (
    <div></div>
  ) : loadedPerformance.type === 'performance' ? (
    <>
      <Button
        size="small"
        color="dark"
        onClick={() => setIsDeleteModalOpen(true)}
        className="flex items-center gap-1"
      >
        <Trash2 className="w-4 h-4" />
        삭제
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={() => onEdit?.(loadedPerformance)}
        className="flex items-center gap-1"
      >
        <Edit2 className="w-4 h-4" />
        수정
      </Button>
    </>
  ) : (
    <>
      <Button
        size="small"
        color="dark"
        onClick={() => setIsDeleteModalOpen(true)}
        className="flex items-center gap-1"
      >
        <Trash2 className="w-4 h-4" />
        삭제
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={() => onEdit?.(loadedPerformance)}
        className="flex items-center gap-1"
      >
        <Edit2 className="w-4 h-4" />
        수정
      </Button>
    </>
  )

  const handleDeleteConfirm = async () => {
    if (!loadedPerformance) return

    setIsDeleting(true)
    setDeleteError(null)

    try {
      await onDelete?.(loadedPerformance)
      setIsDeleteModalOpen(false)
      onClose()
      onSuccess?.()
    } catch (err) {
      console.error('Failed to delete:', err)

      if (err && typeof err === 'object' && 'status' in err) {
        switch (err.status) {
          case 401:
            setDeleteError('인증이 만료되었습니다. 다시 로그인해주세요.')
            break
          case 404:
            setDeleteError('삭제하려는 항목을 찾을 수 없습니다.')
            break
          case 500:
            setDeleteError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
            break
          default:
            setDeleteError('삭제에 실패했습니다. 다시 시도해주세요.')
        }
      } else {
        setDeleteError('삭제에 실패했습니다. 다시 시도해주세요.')
      }
    } finally {
      setIsDeleting(false)
    }
  }

  const getDeleteMessage = () => {
    const typeLabel = getPerformanceTypeLabel(
      loadedPerformance.type as PerformanceNodeType,
      loadedPerformance.linkType,
    )
    return `${typeLabel}를 삭제하시겠습니까?\n하위 항목까지 모두 삭제됩니다.`
  }

  const typeLabel = getPerformanceTypeLabel(
    loadedPerformance.type as PerformanceNodeType,
    loadedPerformance.linkType,
  )

  return (
    <>
      <SidePanel
        isOpen={isOpen}
        onClose={onClose}
        title={`${typeLabel} 상세`}
        width={800}
        headerActions={headerActions}
      >
        <div
          className="flex flex-col space-y-2"
          style={{
            padding: '24px',
          }}
        >
          {/* 기본정보 */}
          <div className="space-y-2">
            <SectionHeader title="기본정보" />
            <div className="space-y-4" style={{ padding: '0px 4px' }}>
              <InfoField
                label="유형"
                value={
                  <Tag
                    color={getTypeColor(loadedPerformance.type, loadedPerformance.linkType)}
                    size="small"
                  >
                    {typeLabel}
                  </Tag>
                }
              />
              <InfoField
                label="이름"
                value={loadedPerformance.name}
              />
              <InfoField
                label="계층구조"
                value={getHierarchyDisplay(loadedPerformance)}
              />
              {loadedPerformance.sort_key != null && (
                <InfoField
                  label="번호"
                  value={String(loadedPerformance.sort_key)}
                />
              )}

              {/* Group-specific fields */}
              {loadedPerformance.type === 'performance_group' && (
                <InfoField
                  label="달성률"
                  value={
                    <span
                      style={{
                        color: loadedPerformance.achievementRate !== null && loadedPerformance.achievementRate >= 100
                          ? designTokens.colors.success.DEFAULT
                          : loadedPerformance.achievementRate !== null && loadedPerformance.achievementRate >= 80
                          ? designTokens.colors.primary.DEFAULT
                          : loadedPerformance.achievementRate !== null && loadedPerformance.achievementRate >= 60
                          ? designTokens.colors.warning.DEFAULT
                          : designTokens.colors.error.DEFAULT,
                        fontWeight: 600,
                      }}
                    >
                      {formatRate(loadedPerformance.achievementRate)}
                    </span>
                  }
                />
              )}

              {/* Category-specific fields */}
              {loadedPerformance.type === 'performance_category' && (
                <>
                  <InfoField
                    label="달성률"
                    value={
                      <span
                        style={{
                          color: loadedPerformance.achievementRate !== null && loadedPerformance.achievementRate >= 100
                            ? designTokens.colors.success.DEFAULT
                            : loadedPerformance.achievementRate !== null && loadedPerformance.achievementRate >= 80
                            ? designTokens.colors.primary.DEFAULT
                            : loadedPerformance.achievementRate !== null && loadedPerformance.achievementRate >= 60
                            ? designTokens.colors.warning.DEFAULT
                            : designTokens.colors.error.DEFAULT,
                          fontWeight: 600,
                        }}
                      >
                        {formatRate(loadedPerformance.achievementRate)}
                      </span>
                    }
                  />
                  {loadedPerformance.weight !== undefined && (
                    <InfoField
                      label="가중치"
                      value={`${loadedPerformance.weight.toFixed(2)}%`}
                    />
                  )}
                  {loadedPerformance.manual_weight !== null && loadedPerformance.manual_weight !== undefined && (
                    <InfoField
                      label="수동 가중치"
                      value={`${loadedPerformance.manual_weight.toFixed(2)}%`}
                    />
                  )}
                </>
              )}

              {/* Performance Class-specific fields */}
              {loadedPerformance.type === 'performance_class' && (
                <>
                  <InfoField
                    label="목표수치"
                    value={formatValue(loadedPerformance.targetValue)}
                  />
                  <InfoField
                    label="달성수치"
                    value={formatValue(loadedPerformance.actualValue)}
                  />
                  <InfoField
                    label="달성률"
                    value={
                      <span
                        style={{
                          color: loadedPerformance.achievementRate !== null && loadedPerformance.achievementRate >= 100
                            ? designTokens.colors.success.DEFAULT
                            : loadedPerformance.achievementRate !== null && loadedPerformance.achievementRate >= 80
                            ? designTokens.colors.primary.DEFAULT
                            : loadedPerformance.achievementRate !== null && loadedPerformance.achievementRate >= 60
                            ? designTokens.colors.warning.DEFAULT
                            : designTokens.colors.error.DEFAULT,
                          fontWeight: 600,
                        }}
                      >
                        {formatRate(loadedPerformance.achievementRate)}
                      </span>
                    }
                  />
                  {loadedPerformance.weight !== undefined && (
                    <InfoField
                      label="가중치"
                      value={`${loadedPerformance.weight.toFixed(2)}%`}
                    />
                  )}
                  {loadedPerformance.manual_weight !== null && loadedPerformance.manual_weight !== undefined && (
                    <InfoField
                      label="수동 가중치"
                      value={`${loadedPerformance.manual_weight.toFixed(2)}%`}
                    />
                  )}
                  {loadedPerformance.target_figure_formula && (
                    <InfoField
                      label="목표수치 수식"
                      value={loadedPerformance.target_figure_formula}
                    />
                  )}
                  {loadedPerformance.done_figure_formula && (
                    <InfoField
                      label="달성수치 수식"
                      value={loadedPerformance.done_figure_formula}
                    />
                  )}
                </>
              )}

              {/* Performance-specific fields */}
              {loadedPerformance.type === 'performance' && (
                <>
                  {loadedPerformance.status && (
                    <InfoField
                      label="상태"
                      value={loadedPerformance.status === 'incomplete' ? '미완료' : '완료'}
                    />
                  )}
                  {loadedPerformance.assignee && (
                    <InfoField
                      label="담당자"
                      value={loadedPerformance.assignee.name}
                    />
                  )}
                  {loadedPerformance.department && (
                    <InfoField
                      label="부서"
                      value={loadedPerformance.department.name}
                    />
                  )}
                  {loadedPerformance.unit && (
                    <InfoField
                      label="단위"
                      value={loadedPerformance.unit}
                    />
                  )}
                  <InfoField
                    label="목표값"
                    value={formatValue(loadedPerformance.targetValue)}
                  />
                  <InfoField
                    label="실적값"
                    value={formatValue(loadedPerformance.actualValue)}
                  />
                  <InfoField
                    label="달성율"
                    value={
                      <span
                        style={{
                          color: loadedPerformance.achievementRate !== null && loadedPerformance.achievementRate >= 100
                            ? designTokens.colors.success.DEFAULT
                            : loadedPerformance.achievementRate !== null && loadedPerformance.achievementRate >= 80
                            ? designTokens.colors.primary.DEFAULT
                            : loadedPerformance.achievementRate !== null && loadedPerformance.achievementRate >= 60
                            ? designTokens.colors.warning.DEFAULT
                            : designTokens.colors.error.DEFAULT,
                          fontWeight: 600,
                        }}
                      >
                        {formatRate(loadedPerformance.achievementRate)}
                      </span>
                    }
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </SidePanel>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="삭제 확인"
        message={deleteError ?? getDeleteMessage()}
        confirmText="삭제"
        isDeleting={isDeleting}
      />
    </>
  )
}

export default PerformanceDetailPanel
